import { reactive } from 'vue';
import { appState, busyWhile } from '@/appState';
import { AuthSteps, emptyFlowData } from './authTypes';
export const SESSION_EXPIRED_MESSAGE = 'Your login session has expired. Please log in again.';
let configuredApi = null;
/** True once after an answer moved the login onto the code screen, so the screen asks for its code exactly once; never after a page reload */
let codeDue = false;
/** The background logout call; every login call waits for it first */
let pendingLogout = Promise.resolve();
/**
 * The login flow (spec §12): the step the backend last answered, its data, and the calls; a plain reactive object, not Vuex, not Pinia, no storage.
 * The backend owns the flow: every login call applies its result, and the UI only maps its step to a screen.
 * The session and the busy flag are global, in appState; the flow's own state is read only by the auth screens and the router.
 */
export const authFlow = reactive({
    step: AuthSteps.Login,
    flowData: emptyFlowData(),
    /** The flow was loaded from the server at least once, e.g. after a page reload */
    hydrated: false,
    configure(api) {
        configuredApi = api;
    },
    /** The "reload session" call: the session, or the pending step after a reload, or LOGIN */
    async hydrate() {
        const result = await run(whereTheLoginStands);
        authFlow.hydrated = true;
        // a reload lands on the pending step without asking for another code (spec §6)
        codeDue = false;
        return result;
    },
    /** Reads the session again, e.g. once a post-login step is done, so its list of steps is current */
    refreshSession() {
        return run(whereTheLoginStands);
    },
    /** True once after an answer moved the login onto the code screen, so the screen asks for its code exactly once; never after a page reload */
    takeCodeDue() {
        const due = codeDue;
        codeDue = false;
        return due;
    },
    login(email, password, rememberMe) {
        return run((api) => api.login(email, password, rememberMe));
    },
    requestLoginLink(email, rememberMe) {
        return run((api) => api.requestLoginLink(email, rememberMe));
    },
    loginWithLink(userId, token, rememberMe) {
        return run((api) => api.loginWithLink(userId, token, rememberMe));
    },
    setupMfa(channel, phoneNumber) {
        return run((api) => api.setupMfa(channel, phoneNumber));
    },
    verifyMfa(code) {
        return run((api) => api.verifyMfa(code));
    },
    /** The code screen's send: the first code on arrival, and "Resend" */
    sendMfaCode() {
        return run((api) => api.sendMfaCode());
    },
    backToMfaSetup() {
        return run((api) => api.backToMfaSetup());
    },
    /** The login's forced change: no current password, the pre-login cookie is the proof */
    changeForcedPassword(newPassword) {
        return run((api) => api.changeForcedPassword(newPassword));
    },
    requestPasswordReset(email) {
        return run((api) => api.requestPasswordReset(email));
    },
    resetPassword(userId, token, newPassword) {
        return run((api) => api.resetPassword(userId, token, newPassword));
    },
    // My Profile, in the app and in the post-login step: not login steps, so they leave the step as it is, and answer null without the login (401)
    getProfile() {
        return busyWhile(() => requireApi().getProfile());
    },
    saveProfile(profile) {
        return busyWhile(() => requireApi().saveProfile(profile));
    },
    changePassword(currentPassword, newPassword) {
        return busyWhile(() => requireApi().changePassword(currentPassword, newPassword));
    },
    startMfaChange(currentPassword, channel, phoneNumber) {
        return busyWhile(() => requireApi().startMfaChange(currentPassword, channel, phoneNumber));
    },
    verifyMfaChange(code) {
        return busyWhile(() => requireApi().verifyMfaChange(code));
    },
    startEmailChange(currentPassword, newEmail) {
        return busyWhile(() => requireApi().startEmailChange(currentPassword, newEmail));
    },
    verifyEmailChange(code) {
        return busyWhile(() => requireApi().verifyEmailChange(code));
    },
    /**
     * Ends the login here at once: the login start, no session, no round-trip to wait for, so the login screen opens immediately.
     * The server's logout goes on in the background, and the next login call waits for it, so it never drops a newer login's cookies.
     */
    logout() {
        const result = { step: AuthSteps.Login, flowData: emptyFlowData(), sessionData: null };
        apply(result);
        // nothing to read from the server at the login start: the login screen fills itself from the last login (authSetup.ts)
        authFlow.hydrated = true;
        pendingLogout = requireApi()
            .logout()
            .catch((error) => console.error('The logout call failed', error));
        return Promise.resolve(result);
    },
    revokeThisDevice() {
        return run((api) => api.revokeThisDevice());
    },
    revokeAllDevices() {
        return run((api) => api.revokeAllDevices());
    },
    /** The header's institution picker: the answer is the session, so appState (and Vuex through the glue) follows it; null when refused (401), and the session is gone, as after any 401 */
    changeInstitution(institutionId) {
        return busyWhile(async () => {
            const session = await requireApi().changeInstitution(institutionId);
            if (session)
                appState.sessionData = session;
            else
                apply(expiredResult());
            return session;
        });
    },
});
/** The session (api/session) once logged in, else the login's own answer (api/auth/resume) */
async function whereTheLoginStands(api) {
    const session = await api.session();
    return session ? { step: AuthSteps.Success, flowData: emptyFlowData(), sessionData: session } : api.resume();
}
function requireApi() {
    if (!configuredApi)
        throw new Error('authFlow is not configured: call authFlow.configure(api) first.');
    return configuredApi;
}
/** A login call: runs it and applies its result */
function run(call) {
    return busyWhile(async () => {
        await pendingLogout;
        // a 401 means the login or pre-login state is gone: back to the login start
        const result = (await call(requireApi())) ?? expiredResult();
        apply(result);
        return result;
    });
}
function apply(result) {
    // the backend sends no code on the way to the code screen; the screen asks for it (spec §6)
    if (result.step === AuthSteps.MfaVerify && authFlow.step !== AuthSteps.MfaVerify)
        codeDue = true;
    authFlow.step = result.step;
    authFlow.flowData = result.flowData ?? emptyFlowData();
    appState.sessionData = result.sessionData ?? null;
}
function expiredResult() {
    return {
        step: AuthSteps.Login,
        flowData: { ...emptyFlowData(), reason: 'SESSION_EXPIRED', message: SESSION_EXPIRED_MESSAGE },
        sessionData: null,
    };
}
